import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import type { TagsOption } from '~features/shared/tags-selector';

import type { AudienceOption } from '../ui/multi-class-audience-selector';
import type { EmailCampaignStatus } from './types';

interface EmailDraftState {
  campaignId: string | null;
  campaignStatus: EmailCampaignStatus | null;
  activeTemplateId: string | null;
  activeTemplateName: string;
  activeTemplateCategory: string;
  subject: string;
  tagOptions: Array<TagsOption>;
  classOptions: Array<AudienceOption>;
  eventOptions: Array<AudienceOption>;
  designJson: Record<string, unknown> | null;
  html: string | null;
  scheduleAt: string | null;
  scheduleLocked: boolean;
  overrideDefaults: boolean;
  fromName: string;
  replyToEmail: string;
  batchSize: number;
  openTracking: boolean;
  clickTracking: boolean;
  setCampaignId: (id: string | null) => void;
  setCampaignStatus: (status: EmailCampaignStatus | null) => void;
  setActiveTemplate: (template: {
    id: string | null;
    name?: string;
    category?: string;
  }) => void;
  setSubject: (subject: string) => void;
  setTagOptions: (tags: Array<TagsOption>) => void;
  setClassOptions: (classes: Array<AudienceOption>) => void;
  setEventOptions: (events: Array<AudienceOption>) => void;
  setDesignJson: (design: Record<string, unknown> | null) => void;
  setHtml: (html: string | null) => void;
  setScheduleAt: (value: string | null) => void;
  setScheduleLocked: (locked: boolean) => void;
  setOverrideDefaults: (override: boolean) => void;
  setFromName: (value: string) => void;
  setReplyToEmail: (value: string) => void;
  setBatchSize: (value: number) => void;
  setOpenTracking: (value: boolean) => void;
  setClickTracking: (value: boolean) => void;
  resetDraft: () => void;
  hydrateFromDefaults: (defaults: {
    fromName: string;
    replyToEmail: string;
    batchSize: number;
    openTracking: boolean;
    clickTracking: boolean;
  }) => void;
}

const initialState = {
  campaignId: null as string | null,
  campaignStatus: null as EmailCampaignStatus | null,
  activeTemplateId: null as string | null,
  activeTemplateName: '',
  activeTemplateCategory: 'custom',
  subject: '',
  tagOptions: [] as Array<TagsOption>,
  classOptions: [] as Array<AudienceOption>,
  eventOptions: [] as Array<AudienceOption>,
  designJson: null as Record<string, unknown> | null,
  html: null as string | null,
  scheduleAt: null as string | null,
  scheduleLocked: false,
  overrideDefaults: false,
  fromName: '',
  replyToEmail: '',
  batchSize: 50,
  openTracking: true,
  clickTracking: false,
};

export const useEmailDraftStore = create<EmailDraftState>()(
  persist(
    (set, get) => ({
      ...initialState,
      setCampaignId: (campaignId) => {
        set({ campaignId });
      },
      setCampaignStatus: (campaignStatus) => {
        set({ campaignStatus });
      },
      setActiveTemplate: ({ id, name, category }) => {
        set({
          activeTemplateId: id,
          activeTemplateName: name ?? '',
          activeTemplateCategory: category ?? 'custom',
        });
      },
      setSubject: (subject) => {
        set({ subject });
      },
      setTagOptions: (tagOptions) => {
        set({ tagOptions });
      },
      setClassOptions: (classOptions) => {
        set({ classOptions });
      },
      setEventOptions: (eventOptions) => {
        set({ eventOptions });
      },
      setDesignJson: (designJson) => {
        set({ designJson });
      },
      setHtml: (html) => {
        set({ html });
      },
      setScheduleAt: (scheduleAt) => {
        set({ scheduleAt });
      },
      setScheduleLocked: (scheduleLocked) => {
        set({ scheduleLocked });
      },
      setOverrideDefaults: (overrideDefaults) => {
        set({ overrideDefaults });
      },
      setFromName: (fromName) => {
        set({ fromName });
      },
      setReplyToEmail: (replyToEmail) => {
        set({ replyToEmail });
      },
      setBatchSize: (batchSize) => {
        set({ batchSize });
      },
      setOpenTracking: (openTracking) => {
        set({ openTracking });
      },
      setClickTracking: (clickTracking) => {
        set({ clickTracking });
      },
      resetDraft: () => {
        set({ ...initialState });
      },
      hydrateFromDefaults: (defaults) => {
        if (get().overrideDefaults) {
          return;
        }
        set({
          fromName: defaults.fromName,
          replyToEmail: defaults.replyToEmail,
          batchSize: defaults.batchSize,
          openTracking: defaults.openTracking,
          clickTracking: defaults.clickTracking,
        });
      },
    }),
    {
      name: 'enrollio-email-composer-draft',
      partialize: (state) => ({
        campaignId: state.campaignId,
        campaignStatus: state.campaignStatus,
        activeTemplateId: state.activeTemplateId,
        activeTemplateName: state.activeTemplateName,
        activeTemplateCategory: state.activeTemplateCategory,
        subject: state.subject,
        tagOptions: state.tagOptions,
        classOptions: state.classOptions,
        eventOptions: state.eventOptions,
        designJson: state.designJson,
        html: state.html,
        scheduleAt: state.scheduleAt,
        scheduleLocked: state.scheduleLocked,
        overrideDefaults: state.overrideDefaults,
        fromName: state.fromName,
        replyToEmail: state.replyToEmail,
        batchSize: state.batchSize,
        openTracking: state.openTracking,
        clickTracking: state.clickTracking,
      }),
      version: 4,
      migrate: (persisted, version) => {
        const state = persisted as Record<string, unknown>;
        // Drop legacy parentOptions from v1 draft storage.
        const { parentOptions: _removed, ...rest } = state;
        return {
          ...rest,
          classOptions: (state.classOptions as Array<AudienceOption>) ?? [],
          eventOptions: (state.eventOptions as Array<AudienceOption>) ?? [],
          // v3: override must default off (was sometimes left on in local drafts)
          overrideDefaults:
            version < 3 ? false : Boolean(state.overrideDefaults),
          campaignStatus:
            (state.campaignStatus as EmailCampaignStatus | null) ?? null,
        };
      },
    },
  ),
);
